import { useState, useCallback, useReducer } from 'react';
import axios from 'axios';
import { UserManagementData, AdminFilters } from '../types/admin';
import { UserRole } from '../types/auth';

interface UserManagementState {
  users: UserManagementData[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: AdminFilters;
  loading: boolean;
  error: string | null;
}

type UserManagementAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { users: UserManagementData[]; totalCount: number } }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'UPDATE_USER_SUCCESS'; payload: UserManagementData }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_FILTERS'; payload: AdminFilters }
  | { type: 'CLEAR_ERROR' };

const initialState: UserManagementState = {
  users: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  filters: {},
  loading: false,
  error: null,
};

function userManagementReducer(
  state: UserManagementState,
  action: UserManagementAction
): UserManagementState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        users: action.payload.users,
        totalCount: action.payload.totalCount,
        loading: false,
      };
    case 'FETCH_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'UPDATE_USER_SUCCESS':
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.id ? action.payload : user
        ),
      };
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: action.payload, currentPage: 1 };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export const useUserManagement = () => {
  const [state, dispatch] = useReducer(userManagementReducer, initialState);

  const fetchUsers = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      const params = new URLSearchParams({
        page: state.currentPage.toString(),
        limit: state.pageSize.toString(),
        ...state.filters.userSearch && { search: state.filters.userSearch },
        ...state.filters.userRole && state.filters.userRole !== 'all' && { userRole: state.filters.userRole },
        ...state.filters.dateRange?.start && { startDate: state.filters.dateRange.start },
        ...state.filters.dateRange?.end && { endDate: state.filters.dateRange.end },
      });

      const response = await axios.get(`/api/admin/users?${params}`);
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: {
          users: response.data.data,
          totalCount: response.data.meta.pagination.total,
        },
      });
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error?.message || error.response?.data?.message || 'ユーザー一覧の取得に失敗しました'
        : 'ユーザー一覧の取得に失敗しました';
      dispatch({ type: 'FETCH_ERROR', payload: errorMessage });
    }
  }, [state.currentPage, state.pageSize, state.filters]);

  const updateUserRole = useCallback(async (userId: number, newRole: UserRole) => {
    try {
      const response = await axios.put(`/api/admin/users/${userId}/role`, {
        userRole: newRole,
      });
      dispatch({ type: 'UPDATE_USER_SUCCESS', payload: response.data.data.user });
      return true;
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error?.message || error.response?.data?.message || 'ユーザー権限の更新に失敗しました'
        : 'ユーザー権限の更新に失敗しました';
      dispatch({ type: 'FETCH_ERROR', payload: errorMessage });
      return false;
    }
  }, []);

  const toggleUserActive = useCallback(async (userId: number, isActive: boolean) => {
    try {
      const status = isActive ? 'active' : 'inactive';
      const response = await axios.put(`/api/admin/users/${userId}/status`, {
        status,
      });
      dispatch({ type: 'UPDATE_USER_SUCCESS', payload: response.data.data.user });
      return true;
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error?.message || error.response?.data?.message || 'ユーザー状態の更新に失敗しました'
        : 'ユーザー状態の更新に失敗しました';
      dispatch({ type: 'FETCH_ERROR', payload: errorMessage });
      return false;
    }
  }, []);

  const setPage = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  const setFilters = useCallback((filters: AdminFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return {
    ...state,
    fetchUsers,
    updateUserRole,
    toggleUserActive,
    setPage,
    setFilters,
    clearError,
  };
};